/**
 * Plan lines API (TMMAPP-0010 / TMMAPP-0011, design §8.5 / §10.9) — wire types from
 * `src/api/generated/`; normalized into the module's domain type. Write callers refetch the
 * list and the projection after every 2xx. TMMAPP-0112 (TMMAPI-0148, MM-D-122): a line sits on an
 * envelope or on the virtual account itself (an "expected item"); an unresolved past one-off is
 * `overdue`; settle / unsettle marks an occurrence received (optionally linked to a part), and the
 * virtual account's match suggestions are proposals a writer confirms — never applied automatically.
 */
import { request } from './client';
import { ApiShapeError } from './errors';
import type {
  PlanLineCreateRequest as WirePlanLineCreate,
  PlanLinePatchRequest as WirePlanLinePatch,
  PlanLineResponse as WirePlanLine,
  PlanLineSettleRequest,
  PlanLineSpreadRequest as WirePlanLineSpread,
  PlanMatchCandidate as WirePlanMatchCandidate,
  PlanMatchDismissalRequest,
  PlanMatchesResponse as WirePlanMatches,
} from './generated';
import { normalizeRecurrence, toWireRecurrence, type RecurrenceRule } from './planned';

export type PlanLineKind = 'INCOME' | 'EXPENSE';
export type SpreadCadence = 'MONTHLY' | 'WEEKLY';

export interface PlanLineCreateRequest {
  /** Required by tmm-api since TMMAPI-0143 (trimmed, 1-120). */
  name: string;
  kind?: PlanLineKind | undefined;
  amountMinor: number;
  note?: string | undefined;
  occursOn?: string | undefined;
  recurrence?: RecurrenceRule | undefined;
  /** "Add anyway" after a `409 PLAN_LINE_LIKELY_DUPLICATE` (TMMAPI-0202, MM-D-154). */
  confirmDuplicate?: boolean | undefined;
}

export interface PlanLinePatchRequest {
  name?: string | undefined;
  kind?: PlanLineKind | undefined;
  amountMinor?: number | undefined;
  note?: string | undefined;
  occursOn?: string | undefined;
  recurrence?: RecurrenceRule | undefined;
  /** "Save anyway" after a `409 PLAN_LINE_LIKELY_DUPLICATE` (TMMAPI-0202, MM-D-154). */
  confirmDuplicate?: boolean | undefined;
}

/** The bank series' slice a line likely repeats (TMMAPI-0202, MM-D-154), as the reader reads it. */
export interface LikelyDuplicateOf {
  /** Absent for a published reader with no alternate name. */
  seriesName: string | null;
  /** Full readers only (MM-D-130). */
  accountName: string | null;
  amountMinor: number;
  nextOn: string;
}

export interface PlanLineSpreadRequest {
  /** Optional on spread (TMMAPI-0143): the lines carry no name when it is left out. */
  name?: string | undefined;
  totalMinor: number;
  startOn: string;
  endOn: string;
  cadence: SpreadCadence;
  note?: string | undefined;
}

export interface SettledOccurrence {
  occursOn: string;
  allocationId: number | null;
}

export interface PlanLine {
  id: number;
  /** The envelope it sits on; `null` for a line on the virtual account itself (TMMAPI-0148). */
  envelopeId: number | null;
  /** The virtual account it sits on directly; `null` for an envelope's line. */
  virtualAccountId: number | null;
  kind: PlanLineKind;
  occursOn: string;
  amountMinor: number;
  /** The line's name (TMMAPI-0143); null on a line created before names existed, or not served. */
  name: string | null;
  note: string | null;
  spreadGroup: string | null;
  recurrence: RecurrenceRule;
  nextOccurrences: string[];
  dismissed: string[];
  /** An unresolved one-off dated before today (the owner's zone), drawn at today (MM-D-122). */
  overdue: boolean;
  /** The occurrences marked received, each with the part it was linked to, if any. */
  settled: SettledOccurrence[];
  /** Computed at read; null when the line repeats no bank series' slice. Nothing changes it. */
  likelyDuplicateOf: LikelyDuplicateOf | null;
}

function fallbackRecurrence(occursOn: string): RecurrenceRule {
  return {
    frequency: 'ONCE',
    interval: null,
    byWeekdays: null,
    byMonthDays: null,
    byWeekOrdinal: null,
    byOrdinalWeekday: null,
    byMonth: null,
    startOn: occursOn,
    endsOn: null,
    occurrenceCount: null,
  };
}

/**
 * A plan line; `null` (dropped, warned in DEV) without a holder (an envelopeId or, TMMAPI-0148, a
 * virtualAccountId). A recurrence this app doesn't know yet reads as a one-off on `occursOn`.
 */
export function normalizePlanLine(w: WirePlanLine): PlanLine | null {
  const envelopeId = w.envelopeId ?? null;
  const virtualAccountId = w.virtualAccountId ?? null;
  if (envelopeId === null && virtualAccountId === null) {
    if (import.meta.env.DEV) console.warn('planLines: row without a holder dropped', w.id);
    return null;
  }
  return {
    id: w.id,
    envelopeId,
    virtualAccountId: envelopeId === null ? virtualAccountId : null,
    kind: w.kind === 'INCOME' ? 'INCOME' : 'EXPENSE',
    occursOn: w.occursOn,
    // tmm-api serves a magnitude and the direction in `kind`: an expense reads negative here.
    amountMinor: w.kind === 'INCOME' ? Math.abs(w.amountMinor) : -Math.abs(w.amountMinor),
    name: w.name ?? null,
    note: w.note ?? null,
    spreadGroup: w.spreadGroup ?? null,
    recurrence: normalizeRecurrence(w.recurrence) ?? fallbackRecurrence(w.occursOn),
    nextOccurrences: w.nextOccurrences,
    dismissed: w.dismissed,
    overdue: w.overdue ?? false,
    settled: w.settled.map((row) => ({
      occursOn: row.occursOn,
      allocationId: row.allocationId ?? null,
    })),
    likelyDuplicateOf:
      w.likelyDuplicateOf == null
        ? null
        : {
            seriesName: w.likelyDuplicateOf.seriesName ?? null,
            accountName: w.likelyDuplicateOf.accountName ?? null,
            amountMinor: w.likelyDuplicateOf.amountMinor,
            nextOn: w.likelyDuplicateOf.nextOn,
          },
  };
}

export function normalizePlanLineList(value: readonly WirePlanLine[]): PlanLine[] {
  return value.flatMap((row) => {
    const line = normalizePlanLine(row);
    return line ? [line] : [];
  });
}

function requiredLine(wire: WirePlanLine): PlanLine {
  const line = normalizePlanLine(wire);
  if (!line) throw new ApiShapeError('planLine has no envelopeId or virtualAccountId');
  return line;
}

export const planLinesApi = {
  /** GET /v1/envelopes/{envelopeId}/planned-expenses. */
  async list(envelopeId: number): Promise<PlanLine[]> {
    return normalizePlanLineList(
      await request<WirePlanLine[]>('GET', `/v1/envelopes/${envelopeId}/planned-expenses`),
    );
  },
  /** POST /v1/envelopes/{envelopeId}/planned-expenses. */
  async create(envelopeId: number, body: PlanLineCreateRequest): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>('POST', `/v1/envelopes/${envelopeId}/planned-expenses`, {
        json: createBody(body),
      }),
    );
  },
  /** PATCH /v1/envelopes/{envelopeId}/planned-expenses/{expenseId}. */
  async update(envelopeId: number, lineId: number, body: PlanLinePatchRequest): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>(
        'PATCH',
        `/v1/envelopes/${envelopeId}/planned-expenses/${lineId}`,
        { json: lineBody(body) },
      ),
    );
  },
  /** DELETE /v1/envelopes/{envelopeId}/planned-expenses/{expenseId}. Opaque; caller refetches. */
  async remove(envelopeId: number, lineId: number): Promise<void> {
    await request<unknown>('DELETE', `/v1/envelopes/${envelopeId}/planned-expenses/${lineId}`);
  },
  /** POST /v1/envelopes/{envelopeId}/planned-expenses/spread. */
  async spread(envelopeId: number, body: PlanLineSpreadRequest): Promise<PlanLine[]> {
    return normalizePlanLineList(
      await request<WirePlanLine[]>('POST', `/v1/envelopes/${envelopeId}/planned-expenses/spread`, {
        json: spreadBody(body),
      }),
    );
  },
  /** DELETE …/spread/{spreadGroup}. Opaque; caller refetches. */
  async removeSpread(envelopeId: number, spreadGroup: string): Promise<void> {
    await request<unknown>(
      'DELETE',
      `/v1/envelopes/${envelopeId}/planned-expenses/spread/${encodeURIComponent(spreadGroup)}`,
    );
  },
  /** POST …/{expenseId}/occurrences/{originalOn}/dismiss. */
  async dismiss(envelopeId: number, lineId: number, originalOn: string): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>(
        'POST',
        `/v1/envelopes/${envelopeId}/planned-expenses/${lineId}/occurrences/${originalOn}/dismiss`,
      ),
    );
  },
  /** POST …/{expenseId}/occurrences/{originalOn}/restore. */
  async restore(envelopeId: number, lineId: number, originalOn: string): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>(
        'POST',
        `/v1/envelopes/${envelopeId}/planned-expenses/${lineId}/occurrences/${originalOn}/restore`,
      ),
    );
  },
  /** POST …/{expenseId}/settle (TMMAPI-0148): mark an occurrence received, optionally linked to a part. */
  async settle(envelopeId: number, lineId: number, body: SettleRequest): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>(
        'POST',
        `/v1/envelopes/${envelopeId}/planned-expenses/${lineId}/settle`,
        { json: settleBody(body) },
      ),
    );
  },
  /** POST …/{expenseId}/unsettle (TMMAPI-0148). */
  async unsettle(envelopeId: number, lineId: number, occurrenceOn?: string): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>(
        'POST',
        `/v1/envelopes/${envelopeId}/planned-expenses/${lineId}/unsettle`,
        { json: occurrenceOn !== undefined ? { occurrenceOn } : {} },
      ),
    );
  },
};

/** Settle: `occurrenceOn` is required for a recurring line; `allocationId` links the part it matched. */
export interface SettleRequest {
  occurrenceOn?: string | undefined;
  allocationId?: number | undefined;
}

function settleBody(body: SettleRequest): PlanLineSettleRequest {
  const w: PlanLineSettleRequest = {};
  if (body.occurrenceOn !== undefined) w.occurrenceOn = body.occurrenceOn;
  if (body.allocationId !== undefined) w.allocationId = body.allocationId;
  return w;
}

/** A PATCH body: only the keys the caller set; the rule goes on the wire without nulls. */
function lineBody(body: PlanLinePatchRequest): WirePlanLinePatch {
  const w: WirePlanLinePatch = {};
  if (body.name !== undefined) w.name = body.name;
  if (body.kind !== undefined) w.kind = body.kind;
  if (body.amountMinor !== undefined) w.amountMinor = body.amountMinor;
  if (body.note !== undefined) w.note = body.note;
  if (body.occursOn !== undefined) w.occursOn = body.occursOn;
  if (body.recurrence !== undefined) w.recurrence = toWireRecurrence(body.recurrence);
  if (body.confirmDuplicate === true) w.confirmDuplicate = true;
  return w;
}

function createBody(body: PlanLineCreateRequest): WirePlanLineCreate {
  return { ...lineBody(body), name: body.name, amountMinor: body.amountMinor };
}

function spreadBody(body: PlanLineSpreadRequest): WirePlanLineSpread {
  const w: WirePlanLineSpread = {
    totalMinor: body.totalMinor,
    startOn: body.startOn,
    endOn: body.endOn,
    cadence: body.cadence,
  };
  if (body.name !== undefined) w.name = body.name;
  if (body.note !== undefined) w.note = body.note;
  return w;
}

/** A posted part that may be an expected item's money (TMMAPI-0148): a suggestion, never applied. */
export interface PlanMatchCandidate {
  allocationId: number;
  transactionId: number;
  /** Signed, in the virtual account owner's currency. */
  amountMinor: number;
  /** The served name for the reader (the alternate name, else the published payee). */
  payee: string | null;
  postedOn: string;
  envelopeId: number | null;
  unassigned: boolean;
}

export interface PlanMatchItem {
  planLineId: number;
  /** The envelope the line sits on; `null` for a line on the virtual account. */
  envelopeId: number | null;
  kind: PlanLineKind;
  name: string | null;
  /** Signed by `kind`, as a plan line's amount. */
  amountMinor: number;
  occursOn: string;
  overdue: boolean;
  candidates: PlanMatchCandidate[];
}

function normalizeCandidate(w: WirePlanMatchCandidate): PlanMatchCandidate {
  return {
    allocationId: w.allocationId,
    transactionId: w.transactionId,
    amountMinor: w.amountMinor,
    payee: w.payee ?? null,
    postedOn: w.postedOn,
    envelopeId: w.envelopeId ?? null,
    unassigned: w.unassigned ?? false,
  };
}

/** The match list; an item without a candidate is left out (nothing to suggest). */
export function normalizePlanMatches(value: WirePlanMatches): PlanMatchItem[] {
  return value.items.flatMap((w) => {
    if (w.candidates.length === 0) return [];
    const kind: PlanLineKind = w.kind === 'INCOME' ? 'INCOME' : 'EXPENSE';
    return [
      {
        planLineId: w.planLineId,
        envelopeId: w.envelopeId ?? null,
        kind,
        name: w.name ?? null,
        amountMinor: kind === 'INCOME' ? Math.abs(w.amountMinor) : -Math.abs(w.amountMinor),
        occursOn: w.occursOn,
        overdue: w.overdue ?? false,
        candidates: w.candidates.map(normalizeCandidate),
      },
    ];
  });
}

const vaLines = (virtualAccountId: number) => `/v1/virtual-accounts/${virtualAccountId}/plan-lines`;

/**
 * Expected items on the virtual account itself (TMMAPI-0148, MM-D-122): every member reads them; the
 * owner or a Manager writes (a Viewer gets `403 FORBIDDEN`).
 */
export const virtualAccountPlanLinesApi = {
  /** GET /v1/virtual-accounts/{id}/plan-lines. */
  async list(virtualAccountId: number): Promise<PlanLine[]> {
    return normalizePlanLineList(await request<WirePlanLine[]>('GET', vaLines(virtualAccountId)));
  },
  /** POST /v1/virtual-accounts/{id}/plan-lines. */
  async create(virtualAccountId: number, body: PlanLineCreateRequest): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>('POST', vaLines(virtualAccountId), { json: createBody(body) }),
    );
  },
  /** PATCH /v1/virtual-accounts/{id}/plan-lines/{lineId}. */
  async update(
    virtualAccountId: number,
    lineId: number,
    body: PlanLinePatchRequest,
  ): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>('PATCH', `${vaLines(virtualAccountId)}/${lineId}`, {
        json: lineBody(body),
      }),
    );
  },
  /** DELETE /v1/virtual-accounts/{id}/plan-lines/{lineId}. Opaque; caller refetches. */
  async remove(virtualAccountId: number, lineId: number): Promise<void> {
    await request<unknown>('DELETE', `${vaLines(virtualAccountId)}/${lineId}`);
  },
  /** POST …/plan-lines/{lineId}/occurrences/{originalOn}/dismiss: the item is not coming. */
  async dismiss(virtualAccountId: number, lineId: number, originalOn: string): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>(
        'POST',
        `${vaLines(virtualAccountId)}/${lineId}/occurrences/${originalOn}/dismiss`,
      ),
    );
  },
  /** POST …/plan-lines/{lineId}/occurrences/{originalOn}/restore. */
  async restore(virtualAccountId: number, lineId: number, originalOn: string): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>(
        'POST',
        `${vaLines(virtualAccountId)}/${lineId}/occurrences/${originalOn}/restore`,
      ),
    );
  },
  /** POST …/plan-lines/{lineId}/settle: Mark received. */
  async settle(virtualAccountId: number, lineId: number, body: SettleRequest): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>('POST', `${vaLines(virtualAccountId)}/${lineId}/settle`, {
        json: settleBody(body),
      }),
    );
  },
  /** POST …/plan-lines/{lineId}/unsettle. */
  async unsettle(
    virtualAccountId: number,
    lineId: number,
    occurrenceOn?: string,
  ): Promise<PlanLine> {
    return requiredLine(
      await request<WirePlanLine>('POST', `${vaLines(virtualAccountId)}/${lineId}/unsettle`, {
        json: occurrenceOn !== undefined ? { occurrenceOn } : {},
      }),
    );
  },
  /** GET /v1/virtual-accounts/{id}/plan-matches: read-only suggestions; writes nothing. */
  async matches(virtualAccountId: number): Promise<PlanMatchItem[]> {
    return normalizePlanMatches(
      await request<WirePlanMatches>(
        'GET',
        `/v1/virtual-accounts/${virtualAccountId}/plan-matches`,
      ),
    );
  },
  /**
   * POST …/plan-matches/dismissals — "Not this one" (TMMAPI-0152 ask 5): the owner or a Manager;
   * idempotent (a `200` answers a create and a replay alike); shared by every member, so the match
   * list leaves the pair out from then on. The answer is not read: the caller refetches.
   */
  async dismissMatch(virtualAccountId: number, body: PlanMatchDismissalRequest): Promise<void> {
    await request<unknown>(
      'POST',
      `/v1/virtual-accounts/${virtualAccountId}/plan-matches/dismissals`,
      { json: body },
    );
  },
  /** DELETE …/plan-matches/dismissals?planLineId&occursOn&transactionId — the undo (`204`). */
  async undismissMatch(virtualAccountId: number, body: PlanMatchDismissalRequest): Promise<void> {
    await request<void>(
      'DELETE',
      `/v1/virtual-accounts/${virtualAccountId}/plan-matches/dismissals`,
      { query: { ...body } },
    );
  },
};
